
<Detail v-if="data" :logo-on-click="logoOnClick">
    <template v-slot:header>
        <div class="row no-gutters">
            <div class="col col-12">
                <h2>
                    {{ data.name }}
                    <span
                        class="badge badge-pill badge-risk"
                        :class="['badge-risk-' + versionRiskLevel(data, version)]"
                        v-b-tooltip.hover
                        :title="hoverTitle(data, version)"
                    >
                        {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                    </span>
                </h2>
                <h3>{{ $t("organization_id") | upper }}: {{ data.ico }}</h3>
            </div>
        </div>
    </template>

    <template #back>
        <router-link :to="{ name: 'suppliers', params: { version: version, hash: hash } }" class="btn btn-inline text-nowrap" tag="button">
            <b-icon icon="arrow-left" />&nbsp;{{ $t("supplierDetail.nav.suppliersList") }}
        </router-link>
    </template>

    <template #breadcrumbs>
        <div>
            <router-link :to="{ name: version, params: { hash: hash } }">{{ data.analysis.name }}</router-link>
            &nbsp;/&nbsp;
            <router-link :to="{ name: 'suppliers', params: { version: version, hash: hash } }">{{
                $t("overview.suppliers.title")
            }}</router-link>
            &nbsp;/&nbsp;
            {{ data.name }}
        </div>
    </template>

    <template v-slot:header_small>
        <div class="row no-gutters">
            <div class="col col-12">
                <h2>
                    {{ data.name }}
                    <span class="badge badge-pill badge-risk" :class="['badge-risk-' + versionRiskLevel(data, version)]">
                        {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                    </span>
                </h2>
            </div>
        </div>
    </template>

    <template v-slot:menu>
        <Menu :nav="navigation" :current="currentNav" :hash="hash" :version="version" @force-nav="(n) => (forceNav = n)" />
    </template>

    <template v-slot:content>
        <section id="overview_section">
            <Summary v-bind="overviewSettings">
                <template v-slot:value(vestnik)="item">{{ item.value | price }}</template>
                <template v-slot:value(profil)="item">{{ item.value | price }}</template>
                <template v-slot:value(rsmluv)="item">{{ item.value | price }}</template>
                <template v-slot:value(esmluv)="item">{{ item.value | price }}</template>
                <template v-slot:value(payments)="item">{{ item.value | price }}</template>
                <template v-slot:value(years)="item"> {{ item.value[0] | integer }} - {{ item.value[1] | integer }} </template>
                <template v-slot:value(won)="item">{{ formatPercentage(item.value) }}</template>
                <template v-slot:value(bids)="item">{{ formatNumber(item.value, 2) }}</template>
            </Summary>
        </section>

        <section id="flat_flags_section">
            <h4 class="section-title">
                {{ $t("supplierDetail.nav.flags") }}
            </h4>

            <EmptySection
                v-show="!hasFlatFlagsData && !hasOtherFlagsData"
                :message="$t('supplierDetail.flags.nodata')"/>

            <div v-show="hasFlatFlagsData">
                <div class="row align-items-end">
                    <div class="col col-1 flag_header">
                        {{ $t("supplierDetail.flags.header.risk") }}
                    </div>
                    <div class="col col-3 flag_header">
                        {{ $t("supplierDetail.flags.header.group") }}
                    </div>
                    <div class="col flag_header">
                        {{ $t("supplierDetail.flags.header.name") }}
                    </div>
                </div>

                <div v-for="(f, i) in flatFlags" :key="'flat-flag-' + i" class="row align-items-center">
                    <div class="col col-12">
                        <SupplierFlatFlag :flag="f" :version="version" />
                    </div>
                </div>
            </div>
            <div v-if="otherSupplierFlagSettings" class="row other-flags">
                <div class="col col-12">
                    <SupplierFlatFlag v-bind="otherSupplierFlagSettings" />
                </div>
            </div>

        </section>

        <section id="won_section" v-show="isSectionEnabled('won')">
            <h4 class="section-title">
                {{ $t("supplierDetail.nav.won") }}
            </h4>

            <TenderTable
                :table-settings="wonTableSettings"
                :sort-options="wonTendersSortOptions"
                :analysis-version="version"
                :analysis-hash="hash"
                :supplier-id="data.id"
                :debounce="filterDebounce"
                ref="won_tenders_data_table"
            />
        </section>

        <section id="lost_section" v-show="isSectionEnabled('lost')">
            <h4 class="section-title">
                {{ $t("supplierDetail.nav.lost") }}
            </h4>

            <TenderTable
                :table-settings="lostTableSettings"
                :sort-options="lostTendersSortOptions"
                :analysis-version="version"
                :analysis-hash="hash"
                :supplier-id="data.id"
                :debounce="filterDebounce"
                ref="lost_tenders_data_table"
            />
        </section>

        <section id="contracts_section" v-show="isSectionEnabled('contracts')">
            <h4 class="section-title">
                {{ $t("supplierDetail.nav.contracts") }}
            </h4>

            <EmptySection v-show="!hasContractsData" :message="$t('supplierDetail.contracts.nodata')"></EmptySection>

            <div v-show="hasContractsData">
                <SupplierDetailCard
                    v-if="showContractsHistogram"
                    class="mb-4"
                    :title="$t('supplierDetail.contracts.histogram.title')"
                    :description="$t('supplierDetail.contracts.histogram.description')"
                >
                    <template #content>
                        <Histogram :data="contractsHistogram" />
                    </template>
                </SupplierDetailCard>

                <SupplierDetailTable
                    :table-settings="contractsTableSettings"
                    :debounce="filterDebounce"
                    :sort-options="contractsSortOptions"
                    ref="contracts_data_table"
                    @row-clicked="
                    (item) => {
                            $router.push({
                                name: 'contract-detail',
                                params: {
                                    version: version,
                                    hash: hash,
                                    slug: item.slug
                                }
                            });
                        }
                    "
                >
                    <template v-slot:cell(risk)="row">
                        <RiskLevelDumpling :value="row.item.risk" :level="versionRiskLevel(row.item, version)" />
                    </template>

                    <template v-slot:cell(title)="row">
                        {{ row.item.title }}
                        <ContractSourceLink :source="row.item.source_url" />
                    </template>

                    <template v-slot:cell(signature_date)="row">{{ row.value | moment("D. M. YYYY") }}</template>

                    <template v-slot:cell(flags)="row">
                        <FlagDetailRowToggler :row="row" :callback="toggleContractRowDetail" />
                    </template>

                    <template v-slot:row-details="row">
                        <FlagDetailRow :row="row" :version="version" />
                    </template>
                </SupplierDetailTable>
            </div>
        </section>

        <section id="icontracts_section" v-show="isSectionEnabled('icontracts')">
            <h4 class="section-title">
                {{ $t("supplierDetail.nav.icontracts") }}
            </h4>

            <EmptySection v-show="!hasIContractsData" :message="$t('supplierDetail.contracts.nodata')"></EmptySection>
            <div v-show="hasIContractsData">

                <SupplierDetailCard
                    v-if="showIContractsHistogram"
                    class="mb-4"
                    :title="$t('supplierDetail.icontracts.histogram.title')"
                    :description="$t('supplierDetail.icontracts.histogram.description')"
                >
                    <template #content>
                        <Histogram :data="icontractsHistogram" />
                    </template>
                </SupplierDetailCard>

                <SupplierDetailTable
                    :table-settings="icontractsTableSettings"
                    :debounce="filterDebounce"
                    :sort-options="contractsSortOptions"
                    ref="icontracts_data_table"
                    @row-clicked="
                    (item) => {
                            $router.push({
                                name: 'contract-detail',
                                params: {
                                    version: version,
                                    hash: hash,
                                    slug: item.slug
                                }
                            });
                        }
                    "
                >
                    <template v-slot:cell(risk)="row">
                        <RiskLevelDumpling :value="row.item.risk" :level="versionRiskLevel(row.item, version)" />
                    </template>

                    <template v-slot:cell(title)="row">
                        {{ row.item.title }}
                        <ContractSourceLink :source="row.item.source_url" />
                    </template>

                    <template v-slot:cell(signature_date)="row">{{ row.value | moment("D. M. YYYY") }}</template>

                    <template v-slot:cell(flags)="row">
                        <FlagDetailRowToggler :row="row" :callback="toggleContractRowDetail" />
                    </template>

                    <template v-slot:row-details="row">
                        <FlagDetailRow :row="row" :version="version" />
                    </template>
                </SupplierDetailTable>
            </div>
        </section>

        <section id="concurrency_section" v-show="isSectionEnabled('concurrency')">
            <h4 class="section-title">
                {{ $t("supplierDetail.nav.concurrency") }}
            </h4>

            <SupplierDetailCard
                v-if="Math.max(...Object.values(data.concurrency_histogram)) > 0 && showConcurrencyHistogram"
                :title="$t('supplierDetail.concurrency.histogram.title')"
                :description="$t('supplierDetail.concurrency.histogram.description')"
            >
                <template #content>
                    <Histogram :data="concurrencyHistogram" ref="concurrency_histogram" />
                </template>
            </SupplierDetailCard>

            <SupplierDetailTable
                :table-settings="concurrencyTableSettings"
                :debounce="filterDebounce"
                :sort-options="contractsSortOptions"
                ref="concurrency_data_table"
                :enable-search="false"
                :enable-sort="false"
                :enable-pagination="false"
                :enable-total-rows="false"
                @row-clicked="
                    (item) => {
                        if (item.is_supplier) {
                            $router.push({
                                name: 'supplier-detail',
                                params: {
                                    version: version,
                                    hash: hash,
                                    slug: item.slug
                                }
                            });
                        }
                    }
                "
            >

                <template v-slot:cell(concurrents)="row">
                    <div class="row">
                        <div class="col-4 align-items-end d-flex">
                            <b>{{ row.item.name }}</b>
                        </div>
                        <div class="col-4 align-items-end d-flex">
                            <b class="d-inline-block text-truncate">{{ data.name }}</b>
                        </div>
                        <div class="col-4 align-items-end d-flex">
                            <b>{{ $t("supplierDetail.concurrency.table.text.otherWins") }}</b>
                        </div>

                        <div class="col-4 align-items-start d-flex">
                            <ConcurrencyCell
                                :total="row.item.total"
                                :number="row.item.won"
                                :price_number="row.item.won_price"
                                :color="'red'"
                            />
                        </div>
                        <div class="col-4 align-items-start d-flex">
                            <ConcurrencyCell
                                :total="row.item.total"
                                :number="row.item.lost"
                                :price_number="row.item.lost_price"
                                :color="'#00000055'"
                            />
                        </div>

                        <div class="col-4 align-items-start d-flex">
                            <ConcurrencyCell
                                :total="row.item.total"
                                :number="row.item.total - row.item.won - row.item.lost"
                                :price_number="row.item.total_price - row.item.won_price - row.item.lost_price"
                                :color="'#437C90'"
                            />
                        </div>
                    </div>
                </template>


                <template v-slot:cell(flags_count)="row">
                    <span class="badge badge-pill badge-danger">{{ row.value }}</span>
                </template>

                <template v-slot:cell(flags)="row">
                    <FlagDetailRowToggler :row="row" :callback="toggleConcurrencyRowDetail" />
                </template>

                <template v-slot:row-details="row">
                    <FlagDetailRow :row="row" :version="version" />
                </template>
            </SupplierDetailTable>
        </section>

        <section id="payments_section" v-show="isSectionEnabled('payments')">
            <InvoiceTable
                :version="version"
                :hash="hash"
                :externalData="data"
                :typeId="data.id"
                type="supplier"
            />
        </section>

        <section id="nodata_section" v-show="isSectionEnabled('nodata')">
            <h4 class="section-title">
                {{ $t("supplierDetail.nav.nodata") }}
            </h4>
            <EmptySection :message="$t('supplierDetail.nodata.description', nodataSections)"></EmptySection>
        </section>
    </template>
</Detail>
